// Copyright 2016 Sorama B.V.

import { bindable } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Unit } from "units/Unit";

export class Line {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

class TickText {
    text: string;
    x: number;
    y: number;
    fontsize: number;
    fontcolor: string;
}

export class AxisPartInformationTypes {
    static None = "none";
    static Major = "major";
    static Minor = "minor";
    static Both = "both";
}

export class LinearAxisPart extends PlotPartBase implements IExportable {
    //#region HTMLElements
    private linearAxisSvgElement: SVGGElement;
    //#endregion

    //#region Bindables
    @bindable public ticklabelscolor = "white";
    @bindable public ticklabelsfontsize = 11;
    @bindable public axislabelscolor = "white";
    @bindable public axislabelsfontsize = 12;
    @bindable public fontpadding = 6;
    @bindable public majortickscolor = "black";
    @bindable public minortickscolor = "black";
    @bindable public majorgridlinescolor = "black";
    @bindable public minorgridlinescolor = "black";

    @bindable public majorticksopacity = 1;
    @bindable public minorticksopacity = 0.2;
    @bindable public majorgridlinesopacity = 0.2;
    @bindable public minorgridlinesopacity = 0.0;

    @bindable public majorticksize = 6;
    @bindable public minorticksize = 3;
    @bindable public xaxisunit: Unit;
    @bindable public yaxisunit: Unit;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showlabels = "xy";

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showticks = "xy";

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showtickstype: string = AxisPartInformationTypes.Both;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showticklabels = "xy";

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showticklabelstype: string = AxisPartInformationTypes.Both;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public fixedtickplacement = false;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public majortickcountx = 10;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public majortickcounty = 10;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public minortickcountx = 10;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public minortickcounty = 10;

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showgridlines = "";

    @bindable({ changeHandler: "onRedrawPropertyChanged" })
    public showgridlinestype: string = AxisPartInformationTypes.Both;

    @bindable()
    public borderthickness = 2;

    @bindable
    public xaxisticklabelsignificantdigits = 4;

    @bindable
    public yaxisticklabelsignificantdigits = 5;

    //#endregion

    //#region Fields
    private useXBaseUnit = false;
    private useYBaseUnit = false;

    private _xAxisUnit: Unit;
    private _yAxisUnit: Unit;

    public majorTicks: Line[];
    public minorTicks: Line[];
    public xTickTexts: TickText[];
    public yTickTexts: TickText[];
    public xAxisLabel: TickText;
    public yAxisLabel: TickText;
    public majorGridLines: Line[];
    public minorGridLines: Line[];

    public viewBox = "0 0 1 1";

    //#endregion

    //#region Constructor and Initialisation

    constructor() {
        super(0);
    }

    public bind(context: IPlotParent): void {
        super.bind(context);

        // Make sure all bound values are correct, aurelia fails to call the change callbacks the first times <_<
        this.onShowTicksChanged(this.showticks, "xy");
        this.onShowLabelsChanged(this.showlabels, "xy");
        this.onShowTickLabelsChanged(this.showticklabels, "xy");

        this.useXBaseUnit = this.xaxisunit ? false : true;
        this.useYBaseUnit = this.yaxisunit ? false : true;

        this._xAxisUnit = this.xaxisunit;
        this._yAxisUnit = this.yaxisunit;
    }

    //#endregion

    //#region Change Handlers
    private onShowTicksChanged(newValue: string, oldValue: string) {
        if (!this.isValidXYSelector(newValue)) {
            console.error("Invalid XY selector");
            this.showticks = oldValue;
            return;
        }

        this.onRedrawPropertyChanged();
    }

    private onShowLabelsChanged(newValue: string, oldValue: string) {
        if (!this.isValidXYSelector(newValue)) {
            console.error("Invalid XY selector");
            this.showlabels = oldValue;
            return;
        }

        this.onRedrawPropertyChanged();
    }

    private onShowTickLabelsChanged(newValue: string, oldValue: string) {
        if (!this.isValidXYSelector(newValue)) {
            console.error("Invalid XY selector");
            this.showticklabels = oldValue;
            return;
        }

        this.onRedrawPropertyChanged();
    }

    private xaxisunitChanged(newValue: Unit, oldValue: Unit) {
        this.useXBaseUnit = newValue ? false : true;
        if (newValue) {
            this._xAxisUnit = newValue;
        }
        this.onRedrawPropertyChanged();
    }

    private yaxisunitChanged(newValue: Unit, oldValue: Unit) {
        this.useYBaseUnit = newValue ? false : true;
        if (newValue) {
            this._yAxisUnit = newValue;
        }
        this.onRedrawPropertyChanged();
    }

    private onYAxisChanged(newValue: Unit, oldValue: Unit) {
        this.useYBaseUnit = newValue ? false : true;
        if (newValue) {
            this._yAxisUnit = newValue;
        }
        this.onRedrawPropertyChanged();
    }

    private onRedrawPropertyChanged() {
        this.plotParent.requestMeasure(true);
        this.updateControl();
    }

    public onMeasureMargin(): Margin {
        const margin = new Margin(
            this.borderthickness / 2,
            this.borderthickness / 2,
            this.borderthickness / 2,
            this.borderthickness / 2
        );
        if (this.showticklabels) {
            if (this.showticklabels.toLowerCase().indexOf("x") !== -1) {
                margin.bottom += this.ticklabelsfontsize + this.fontpadding;
            }

            if (this.showticklabels.toLowerCase().indexOf("y") !== -1) {
                margin.left += this.ticklabelsfontsize + this.fontpadding;
            }
        }

        if (this.showlabels) {
            if (
                this.showlabels.toLowerCase().indexOf("x") !== -1 &&
                this._xAxisUnit &&
                !this.isUnitless(this._xAxisUnit)
            ) {
                margin.bottom += this.axislabelsfontsize + this.fontpadding;
            }

            if (
                this.showlabels.toLowerCase().indexOf("y") !== -1 &&
                this._yAxisUnit &&
                !this.isUnitless(this._yAxisUnit)
            ) {
                margin.left += this.axislabelsfontsize + this.fontpadding;
            }
        }

        return margin;
    }

    public onSize(size: Size, margin: Margin): void {
        // Wait before setting the size until we get a valid size
        if (size.width - margin.left - margin.right <= 0 || size.height - margin.top - margin.bottom <= 0) return;

        // Check for actual change
        if (
            this.size &&
            this.margin &&
            Math.abs(this.size.width - size.width) < 1e-5 &&
            Math.abs(this.size.height - size.height) < 1e-5 &&
            Math.abs(this.margin.left - margin.left) < 1e-5 &&
            Math.abs(this.margin.top - margin.top) < 1e-5 &&
            Math.abs(this.margin.right - margin.right) < 1e-5 &&
            Math.abs(this.margin.bottom - margin.bottom) < 1e-5
        )
            return; // Ignore update

        this.size = size;
        this.margin = margin;
        this.actualSize = new Size(size.width - margin.left - margin.right, size.height - margin.top - margin.bottom);

        this.viewBox = -margin.left + " " + -margin.top + " " + this.size.width + " " + this.size.height;

        this.updateControl();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        // Check for actual change
        if (
            this.viewPort &&
            this.viewPortBounds &&
            Math.abs(this.viewPort.x - viewPort.x) < 1e-5 &&
            Math.abs(this.viewPort.y - viewPort.y) < 1e-5 &&
            Math.abs(this.viewPort.width - viewPort.width) < 1e-5 &&
            Math.abs(this.viewPort.height - viewPort.height) < 1e-5 &&
            Math.abs(this.viewPortBounds.x - viewPortBounds.x) < 1e-5 &&
            Math.abs(this.viewPortBounds.y - viewPortBounds.y) < 1e-5 &&
            Math.abs(this.viewPortBounds.width - viewPortBounds.width) < 1e-5 &&
            Math.abs(this.viewPortBounds.height - viewPortBounds.height) < 1e-5 &&
            (!this.useXBaseUnit || (!this.useXBaseUnit && units.x == this._xAxisUnit)) &&
            (!this.useYBaseUnit || (!this.useYBaseUnit && units.y == this._yAxisUnit))
        )
            return; // Ignore update

        super.onViewPortComputed(viewPort, viewPortBounds, units);

        const prevX = this._xAxisUnit;
        const prevY = this._yAxisUnit;

        // Set the correct units
        if (this.useXBaseUnit) {
            this._xAxisUnit = units.x;
        }

        if (this.useYBaseUnit) {
            this._yAxisUnit = units.y;
        }

        if (
            ((!prevX || !prevX.unit) && this._xAxisUnit && this._xAxisUnit.unit) ||
            ((!prevY || !prevY.unit) && this._yAxisUnit && this._yAxisUnit.unit)
        ) {
            this.plotParent.requestMeasure(true);
        }
        this.updateControl();
    }

    //#endregion

    //#region Update and Draw Functions

    private updateControl() {
        if (
            !this.viewPort ||
            this.viewPort.width === 0 ||
            this.viewPort.height === 0 ||
            !this.actualSize ||
            !this.margin ||
            !this._xAxisUnit ||
            !this._yAxisUnit ||
            !this.sourceUnits ||
            !Number.isFinite(this.viewPort.width) ||
            !Number.isFinite(this.viewPort.height) ||
            !Number.isFinite(this.viewPort.x) ||
            !Number.isFinite(this.viewPort.y)
        )
            return;

        // Determine lines
        if (this.showticks || this.showticklabels) {
            this.generateTicksAndGrid(this.viewPort, this.viewPort, this.actualSize);
        } else {
            this.majorTicks = [];
            this.minorTicks = [];

            this.majorGridLines = [];
            this.minorGridLines = [];

            this.xTickTexts = [];
            this.yTickTexts = [];
        }

        // Determine labels
        if (this.showlabels) {
            this.generateAxisLabels(this.viewPort, this.viewPort, this.actualSize);
        } else {
            this.yAxisLabel = undefined;
            this.xAxisLabel = undefined;
        }
    }

    private generateTicksAndGrid(viewPortOrigin: Pair<number>, viewPortSize: Size, controlSize: Size): void {
        let majorTickStepX = viewPortSize.width / this.majortickcountx;
        let majorTickStepY = viewPortSize.height / this.majortickcounty;

        let tickOffsetX = 0;
        let tickOffsetY = 0;

        const majorTicks: Line[] = [];
        const minorTicks: Line[] = [];

        const majorGridLines: Line[] = [];
        const minorGridLines: Line[] = [];

        const xTickTexts: TickText[] = [];
        const yTickTexts: TickText[] = [];

        if (!this.fixedtickplacement) {
            majorTickStepX = Math.pow(10, Math.ceil(Math.log10(majorTickStepX)));
            majorTickStepY = Math.pow(10, Math.ceil(Math.log10(majorTickStepY)));

            if (Math.round(viewPortSize.width / majorTickStepX) <= 2) majorTickStepX = majorTickStepX / 2;

            if (Math.round(viewPortSize.height / majorTickStepY) <= 2) majorTickStepY = majorTickStepY / 2;

            // Make sure that there is always a tick at 0 (if it is visible)
            tickOffsetX = -(viewPortOrigin.x - Math.round(viewPortOrigin.x / majorTickStepX) * majorTickStepX);
            tickOffsetY = -(viewPortOrigin.y - Math.round(viewPortOrigin.y / majorTickStepY) * majorTickStepY);
        }

        const minorTickStepX = Math.min(majorTickStepX / this.minortickcountx, majorTickStepX);
        const minorTickStepY = Math.min(majorTickStepY / this.minortickcounty, majorTickStepY);

        // Draw X Ticks and GridLines
        let x = viewPortOrigin.x + tickOffsetX;
        for (let i = 0; x - viewPortOrigin.x <= viewPortSize.width; i++, x += minorTickStepX) {
            if (x < viewPortOrigin.x) continue;

            const svgTickCoordinates = this.viewPortToSvgCoordinates({ x: x, y: viewPortOrigin.y });

            if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) continue;

            // Ticks
            if (this.showticks && this.showticks.toLowerCase().indexOf("x") !== -1) {
                if (this.minortickcountx === 0 || i % this.minortickcountx === 0) {
                    // Label side
                    majorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y - this.majorticksize,
                    });

                    // Opposite side
                    majorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y - this.actualSize.height + this.majorticksize,
                        y1: svgTickCoordinates.y - this.actualSize.height,
                    });
                } else {
                    minorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y - this.minorticksize,
                    });

                    // Opposite side
                    minorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y - this.actualSize.height + this.minorticksize,
                        y1: svgTickCoordinates.y - this.actualSize.height,
                    });
                }
            }

            // Gridlines
            if (this.showgridlines && this.showgridlines.toLowerCase().indexOf("x") !== -1) {
                if (
                    (this.minortickcountx === 0 || i % this.minortickcountx === 0) &&
                    (this.showgridlinestype === AxisPartInformationTypes.Major ||
                        this.showgridlinestype === AxisPartInformationTypes.Both)
                ) {
                    majorGridLines.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y - this.actualSize.height,
                    });
                } else if (
                    this.showgridlinestype === AxisPartInformationTypes.Minor ||
                    this.showgridlinestype === AxisPartInformationTypes.Both
                ) {
                    minorGridLines.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y - this.actualSize.height,
                    });
                }
            }
        }

        // Draw Y Ticks and GridLines
        let y = viewPortOrigin.y + tickOffsetY;
        for (let i = 0; y - viewPortOrigin.y <= viewPortSize.height; i++, y += minorTickStepY) {
            if (y < viewPortOrigin.y) continue;

            const svgTickCoordinates = this.viewPortToSvgCoordinates({ x: viewPortOrigin.x, y: y });

            if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) continue;

            // Ticks
            if (this.showticks && this.showticks.toLowerCase().indexOf("y") !== -1) {
                if (this.minortickcounty === 0 || i % this.minortickcounty === 0) {
                    majorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x + this.majorticksize,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });

                    majorTicks.push(<Line>{
                        x0: svgTickCoordinates.x + this.actualSize.width - this.majorticksize,
                        x1: svgTickCoordinates.x + this.actualSize.width,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });
                } else {
                    minorTicks.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x + this.minorticksize,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });

                    minorTicks.push(<Line>{
                        x0: svgTickCoordinates.x + this.actualSize.width - this.minorticksize,
                        x1: svgTickCoordinates.x + this.actualSize.width,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });
                }
            }

            // Gridlines
            if (this.showgridlines && this.showgridlines.toLowerCase().indexOf("y") !== -1) {
                if (
                    (this.minortickcounty === 0 || i % this.minortickcounty === 0) &&
                    (this.showgridlinestype === AxisPartInformationTypes.Major ||
                        this.showgridlinestype === AxisPartInformationTypes.Both)
                ) {
                    majorGridLines.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x + this.actualSize.width,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });
                } else if (
                    this.showgridlinestype === AxisPartInformationTypes.Minor ||
                    this.showgridlinestype === AxisPartInformationTypes.Both
                ) {
                    minorGridLines.push(<Line>{
                        x0: svgTickCoordinates.x,
                        x1: svgTickCoordinates.x + this.actualSize.width,
                        y0: svgTickCoordinates.y,
                        y1: svgTickCoordinates.y,
                    });
                }
            }
        }

        // Draw x tick labels if required
        if (
            this.showticklabels &&
            this.showticklabels.toLowerCase().indexOf("x") !== -1 &&
            this._xAxisUnit.isOfSameUnitFamily(this.sourceUnits.x)
        ) {
            let x = viewPortOrigin.x + tickOffsetX;
            for (let i = 0; x - viewPortOrigin.x <= viewPortSize.width; i++, x += majorTickStepX) {
                if (x < viewPortOrigin.x || x > viewPortOrigin.x + viewPortSize.width) continue;

                const xDisplayUnit = this._xAxisUnit.fromUnit(this.sourceUnits.x, x);
                let displayString = xDisplayUnit.toLocaleString(undefined, {
                    maximumSignificantDigits: this.xaxisticklabelsignificantdigits,
                    useGrouping: false,
                });

                if (displayString.length > this.xaxisticklabelsignificantdigits + 1) {
                    // Use scientific notation
                    displayString = xDisplayUnit.toExponential(1);
                }

                const svgTickCoordinates = this.viewPortToSvgCoordinates({ x: x, y: viewPortOrigin.y });

                if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) continue;

                if (svgTickCoordinates.x < 3 * displayString.length) {
                    svgTickCoordinates.x = 3 * displayString.length;
                }

                if (svgTickCoordinates.x > this.actualSize.width - 3 * displayString.length) {
                    svgTickCoordinates.x = this.actualSize.width - 3 * displayString.length;
                }

                xTickTexts.push({
                    text: displayString,
                    x: svgTickCoordinates.x,

                    // Note: ticklabelsfontsize would normally be divided by two here but because IE does not support central alignment we compensate for this by not dividing
                    y: svgTickCoordinates.y + this.ticklabelsfontsize + this.fontpadding / 2,
                    fontsize: this.ticklabelsfontsize,
                    fontcolor: this.ticklabelscolor,
                });
            }
        }

        // Draw y tick labels if required
        if (
            this.showticklabels &&
            this.showticklabels.toLowerCase().indexOf("y") !== -1 &&
            this._yAxisUnit.isOfSameUnitFamily(this.sourceUnits.y)
        ) {
            let y = viewPortOrigin.y + tickOffsetY;
            for (let i = 0; y - viewPortOrigin.y <= viewPortSize.height; i++, y += majorTickStepY) {
                if (y < viewPortOrigin.y || y > viewPortOrigin.y + viewPortSize.height) continue;

                const yDisplayUnit = this._yAxisUnit.fromUnit(this.sourceUnits.y, y);
                let displayString = yDisplayUnit.toLocaleString(undefined, {
                    maximumSignificantDigits: this.yaxisticklabelsignificantdigits,
                    useGrouping: false,
                });

                if (displayString.length > this.yaxisticklabelsignificantdigits + 1) {
                    // Use scientific notation
                    displayString = yDisplayUnit.toExponential(1);
                }

                const svgTickCoordinates = this.viewPortToSvgCoordinates({ x: viewPortOrigin.x, y: y });

                if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) continue;

                yTickTexts.push({
                    text: displayString,
                    x: svgTickCoordinates.x - this.ticklabelsfontsize / 2 - this.fontpadding / 2,
                    y: svgTickCoordinates.y,
                    fontsize: this.ticklabelsfontsize,
                    fontcolor: this.ticklabelscolor,
                });
            }
        }

        this.majorTicks = majorTicks;
        this.minorTicks = minorTicks;

        this.majorGridLines = majorGridLines;
        this.minorGridLines = minorGridLines;

        this.xTickTexts = xTickTexts;
        this.yTickTexts = yTickTexts;
    }

    private generateAxisLabels(viewPortOrigin: Pair<number>, viewPortSize: Size, controlSize: Size): void {
        if (this._xAxisUnit && !this.isUnitless(this._xAxisUnit)) {
            const svgTickCoordinates = this.viewPortToSvgCoordinates({
                x: viewPortSize.width / 2 + viewPortOrigin.x,
                y: viewPortOrigin.y,
            });

            if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) return;

            this.xAxisLabel = <TickText>{
                text: this._xAxisUnit.toUnitType() + (this._xAxisUnit.unit ? " [" + this._xAxisUnit.unit + "]" : ""),
                x: svgTickCoordinates.x,
                y:
                    svgTickCoordinates.y +
                    (this.showticklabels ? this.ticklabelsfontsize + this.fontpadding : 0) +
                    this.ticklabelsfontsize / 2 +
                    this.fontpadding / 2,
                fontsize: this.axislabelsfontsize,
                fontcolor: this.axislabelscolor,
            };
        }

        if (this._yAxisUnit && !this.isUnitless(this._yAxisUnit)) {
            const svgTickCoordinates = this.viewPortToSvgCoordinates({
                x: viewPortOrigin.x,
                y: viewPortSize.height / 2 + viewPortOrigin.y,
            });

            if (Number.isNaN(svgTickCoordinates.x) || Number.isNaN(svgTickCoordinates.y)) return;

            this.yAxisLabel = <TickText>{
                text: this._yAxisUnit.toUnitType() + (this._yAxisUnit.unit ? " [" + this._yAxisUnit.unit + "]" : ""),
                x:
                    svgTickCoordinates.x -
                    (this.showticklabels ? this.ticklabelsfontsize + this.fontpadding : 0) -
                    this.ticklabelsfontsize / 2 -
                    this.fontpadding / 2,
                y: svgTickCoordinates.y,
                fontsize: this.axislabelsfontsize,
                fontcolor: this.axislabelscolor,
            };
        }
    }

    private isUnitless(unit: Unit) {
        return unit === undefined || unit.isGeneric;
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.linearAxisSvgElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.linearAxisSvgElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        return Promise.resolve(svgs[0]);
    }

    //#endregion

    //#region Helpers

    private isValidXYSelector(value: string) {
        if (value === null || value === undefined || typeof value !== "string") return false;

        const vv = value.toLowerCase();

        if (vv === "" || vv === "x" || vv === "y" || vv === "xy" || vv === "yx") return true;

        return false;
    }

    private viewPortToSvgCoordinates(coordinate: { x: number; y: number }): { x: number; y: number } {
        return {
            x: ((coordinate.x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width,
            y:
                ((this.viewPort.height - (coordinate.y - this.viewPort.y)) / this.viewPort.height) *
                this.actualSize.height,
        };
    }

    //#endregion
}
